import settings from '../config/settings';
import { getAuthHeaders } from './api';

/**
 * Product Cache Utility
 * Handles caching of product data in session storage
 * Uses secure backend endpoints instead of direct WooCommerce API calls
 */

const { 
  productCacheKey: CACHE_KEY, 
  productCacheTimestampKey: CACHE_TIMESTAMP_KEY,
  cacheExpiry: CACHE_EXPIRY_MS 
} = settings.cache;

// Base URL for backend API requests
const API_BASE_URL = settings.api.baseUrl || '';

// localStorage (not sessionStorage) so the daily TTL survives browser/tab restarts
// and refreshes on a real 24h clock instead of "whenever the session resets".
const store = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : {
  getItem: () => null, setItem: () => {}, removeItem: () => {}
};

/**
 * Save products to cache with timestamp
 * @param {Array} products - Array of product objects
 * @returns {boolean} - True if successful, false otherwise
 */
export const saveProductsToCache = (products) => {
  try {
    const cacheData = {
      products,
      timestamp: Date.now()
    };
    store.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log(`Products cached successfully at ${new Date().toISOString()}`);
    return true;
  } catch (error) {
    console.error('Error saving products to cache:', error);
    return false;
  }
};

/**
 * Get products from cache if they exist and aren't expired
 * @returns {Object|null} Cached products and timestamp or null if invalid/expired
 */
export const getProductsFromCache = () => {
  try {
    const cachedData = store.getItem(CACHE_KEY);
    if (!cachedData) {
      console.log('No cached products found');
      return null;
    }

    const { products, timestamp } = JSON.parse(cachedData);
    const cacheAge = Date.now() - timestamp;
    const isExpired = cacheAge > CACHE_EXPIRY_MS;

    console.log(`Cache info - Age: ${Math.round(cacheAge/1000/60)}m, ` +
                `Expires in: ${Math.max(0, Math.round((CACHE_EXPIRY_MS - cacheAge)/1000/60))}m`);

    if (isExpired) {
      console.log('Cache expired, needs refresh');
      return { products, timestamp, isExpired: true };
    }

    return { products, timestamp, isExpired: false };
  } catch (error) {
    console.error('Error reading products from cache:', error);
    return null;
  }
};

/**
 * Check if cache is valid (exists and not expired)
 * @returns {boolean}
 */
export const isCacheValid = () => {
  const cache = getProductsFromCache();
  return cache && !cache.isExpired;
};

/**
 * Check if cache needs refresh (expired or doesn't exist)
 * @returns {boolean}
 */
export const needsCacheRefresh = () => {
  const cache = getProductsFromCache();
  return !cache || cache.isExpired;
};

/**
 * Get the time remaining until cache expires (in milliseconds)
 * @returns {number} Time in milliseconds, or 0 if no cache or already expired
 */
export const getTimeUntilCacheExpiry = () => {
  const cache = getProductsFromCache();
  if (!cache) return 0;
  
  const timeElapsed = Date.now() - cache.timestamp;
  return Math.max(0, CACHE_EXPIRY_MS - timeElapsed);
};

/**
 * Clear the product cache
 * @returns {boolean} - True if successful, false otherwise
 */
export const clearProductCache = () => {
  try {
    store.removeItem(CACHE_KEY);
    console.log('Product cache cleared');
    return true;
  } catch (error) {
    console.error('Error clearing product cache:', error);
    return false;
  }
};

/**
 * Initialize the product cache if needed
 * @returns {Object} Cache status and data
 */
export const initializeProductCache = async () => {
  const cache = getProductsFromCache();
  
  if (!cache || cache.isExpired) {
    // Return a function to refresh the cache
    return {
      needsRefresh: true,
      data: cache?.products || [],
      refresh: async () => {
        try {
          let allProducts = [];
          let page = 1;
          const perPage = 50; // Using 50 items per page as requested
          let hasMore = true;
          
          console.log('Starting to fetch all products...');
          
          // Fetch all pages of products until an empty response is received
          while (hasMore) {
            // Construct the URL using API_BASE_URL from settings - use backend API endpoint
            const endpoint = '/api/kiosk-products';
            const url = new URL(endpoint, API_BASE_URL);
            url.searchParams.append('per_page', perPage);
            url.searchParams.append('page', page);
            
            const urlString = url.toString();
            console.log(`Fetching page ${page}: ${urlString}`);
            
            const response = await fetch(urlString, {
              headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                ...getAuthHeaders()
              }
            });
            
            if (!response.ok) {
              throw new Error(`Failed to fetch products (page ${page}): ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            const products = Array.isArray(data) ? data : [];
            
            console.log(`Page ${page} returned ${products.length} products`);
            
            // If no products returned, we've reached the end
            if (products.length === 0) {
              console.log('No more products found, ending pagination');
              hasMore = false;
              break;
            }
            
            // Add products to our collection
            allProducts = [...allProducts, ...products];
            console.log(`Total products fetched so far: ${allProducts.length}`);
            
            // If we got fewer items than requested, we've reached the last page
            if (products.length < perPage) {
              console.log('Reached the last page of products');
              hasMore = false;
            }
            
            page++;
            
            // Add a small delay between requests to avoid rate limiting
            if (hasMore) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          
          // Store all fetched products in cache
          saveProductsToCache(allProducts);
          
          // Log summary of all products
          console.log('Cached all products:', {
            totalProducts: allProducts.length,
            sampleProduct: allProducts[0] ? {
              id: allProducts[0].id,
              name: allProducts[0].name,
              imageCount: allProducts[0].images?.length || 0,
              firstImage: allProducts[0].images?.[0]?.src
            } : 'No products found'
          });
          
          return {
            success: true,
            products,
            fromCache: false
          };
        } catch (error) {
          console.error('Error refreshing product cache:', error);
          return {
            success: false,
            error: error.message,
            products: cache?.products || []
          };
        }
      }
    };
  }
  
  return {
    needsRefresh: false,
    data: cache.products,
    fromCache: true
  };
};
